import { useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@components/shared/Button'
import { CloseIcon } from '@assets/svgs/CloseIcon'
import { FilterIcon } from '@assets/svgs/FilterIcon'
import { getInitialFilterValue } from '@pages/Preview/components/BigNumbersFilters/helpers/getInitialFilterValue'
import { MultiSelect } from '@components/shared/MultiSelect'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { useUser } from '@hooks/useUser'

import { getInitialMultiFilterValue } from './helpers/getInitialMultiFilterValue'
import { getSingleFilterOption, getTodosFilterValue } from './helpers/getSingleFilterOption'
import { initialFormValues } from './form/initialFormValues'

import type { AvailableFiltersItemProps } from '@services/api/preview/types'
import type { PreviewManagementFiltersFormProps } from './form/types'
import type { PreviewManagementFiltersProps } from './types'

import * as apiPreviewManagement from '@services/api/previewManagement'
import * as S from './styles'

const toSortedKey = (values?: string[]) => [...(values ?? [])].sort().join('|')

export const PreviewManagementFilters = ({ onFilter, onClear, initialValues, children }: PreviewManagementFiltersProps) => {
  const { filiData, setFiliData } = useUser()

  // Com valores iniciais, o form j� nasce com eles e as refs da cascata abaixo come�am nas mesmas
  // chaves � assim os efeitos de "ao trocar X, reseta Y" n�o apagam a sele��o que veio de fora
  const startValues = initialValues ?? initialFormValues
  const pendingInitialValuesRef = useRef(!!initialValues)

  const methods = useForm<PreviewManagementFiltersFormProps>({
    defaultValues: startValues
  })

  const businessUnit = methods.watch('businessUnit')
  const selectedBusinessUnit = businessUnit && businessUnit !== 'all' ? businessUnit : null

  const directorate = methods.watch('directorate') ?? []
  const portfolio = methods.watch('portfolio') ?? []
  const economicGroup = methods.watch('economicGroup') ?? []
  const segment = methods.watch('segment') ?? []
  const serviceType = methods.watch('serviceType') ?? []
  const goName = methods.watch('goName') ?? []

  const hasDirectorateSelection = directorate.length > 0
  const hasPortfolioSelection = portfolio.length > 0
  const hasEconomicGroupSelection = economicGroup.length > 0
  const hasSegmentSelection = segment.length > 0
  const hasGoNameSelection = goName.length > 0

  // Depois do "Limpar", o Tipo de Servi�o fica no estado neutro ("Todos") enquanto o Centro de
  // Lucro continuar em "Todos"; ao escolher um CL espec�fico a op��o �nica volta a ser for�ada
  const clearedFiliRef = useRef<string | null>(null)
  const isClearedState = () => !!clearedFiliRef.current && clearedFiliRef.current === filiData?.filiCode

  const getServiceTypeInitialValue = (options?: AvailableFiltersItemProps[]) =>
    isClearedState() ? getTodosFilterValue(options) : getInitialMultiFilterValue(options ?? [])

  const directorateKey = toSortedKey(directorate)
  const portfolioKey = toSortedKey(portfolio)
  const economicGroupKey = toSortedKey(economicGroup)
  const segmentKey = toSortedKey(segment)
  const goNameKey = toSortedKey(goName)

  // Fonte fixa das op��es de Business Unit e dos valores iniciais do form, escopada pelo CL do header
  const { data: baseFiltersData, isLoading: baseFiltersLoading } = useQuery({
    queryKey: ['preview-management-filters', filiData?.filiCode],
    queryFn: () => apiPreviewManagement.getPreviewManagementFilters({ branchCodes: [filiData!.filiCode] }),
    enabled: !!filiData?.filiCode
  })

  // Diretoria (e Nome GO, que n�o depende de mais nada abaixo) dependem s� do Business Unit
  const { data: afterBusinessUnitData, isLoading: afterBusinessUnitLoading } = useQuery({
    queryKey: ['preview-management-filters', filiData?.filiCode, 'bu', selectedBusinessUnit],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        businessUnit: [selectedBusinessUnit!]
      }),
    enabled: !!filiData?.filiCode && !!selectedBusinessUnit
  })

  const directorateSourceData = selectedBusinessUnit ? afterBusinessUnitData : baseFiltersData
  const directorateSourceLoading = selectedBusinessUnit ? afterBusinessUnitLoading : baseFiltersLoading

  // Grupo Econ�mico depende do Business Unit + Diretoria selecionada
  const { data: afterDirectorateData, isLoading: afterDirectorateLoading } = useQuery({
    queryKey: ['preview-management-filters', filiData?.filiCode, 'bu', selectedBusinessUnit, 'directorate', directorateKey],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        businessUnit: [selectedBusinessUnit!],
        boardOfDirectors: directorate
      }),
    enabled: !!filiData?.filiCode && !!selectedBusinessUnit && hasDirectorateSelection
  })

  const portfolioSourceData =
    selectedBusinessUnit && hasDirectorateSelection ? afterDirectorateData : directorateSourceData
  const portfolioSourceLoading =
    selectedBusinessUnit && hasDirectorateSelection ? afterDirectorateLoading : directorateSourceLoading

  // Grupo Econ�mico depende do Business Unit + Diretoria + Portf�lio selecionados
  const { data: afterPortfolioData, isLoading: afterPortfolioLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        businessUnit: [selectedBusinessUnit!],
        boardOfDirectors: directorate,
        portfolio
      }),
    enabled: !!filiData?.filiCode && !!selectedBusinessUnit && hasPortfolioSelection
  })

  const economicGroupSourceData =
    selectedBusinessUnit && hasPortfolioSelection ? afterPortfolioData : portfolioSourceData
  const economicGroupSourceLoading =
    selectedBusinessUnit && hasPortfolioSelection ? afterPortfolioLoading : portfolioSourceLoading

  // Segmento depende do Business Unit + Diretoria + Grupo Econ�mico selecionados
  const { data: afterEconomicGroupData, isLoading: afterEconomicGroupLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey,
      'economicGroup',
      economicGroupKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        businessUnit: [selectedBusinessUnit!],
        boardOfDirectors: directorate,
        portfolio,
        economicGroup
      }),
    enabled: !!filiData?.filiCode && !!selectedBusinessUnit && hasEconomicGroupSelection
  })

  const segmentSourceData =
    selectedBusinessUnit && hasEconomicGroupSelection ? afterEconomicGroupData : economicGroupSourceData
  const segmentSourceLoading =
    selectedBusinessUnit && hasEconomicGroupSelection ? afterEconomicGroupLoading : economicGroupSourceLoading

  // Tipo de Servi�o depende do Business Unit + Diretoria + Grupo Econ�mico + Segmento selecionados
  const { data: afterSegmentData, isLoading: afterSegmentLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey,
      'economicGroup',
      economicGroupKey,
      'segment',
      segmentKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        businessUnit: [selectedBusinessUnit!],
        boardOfDirectors: directorate,
        portfolio,
        economicGroup,
        product: segment
      }),
    enabled: !!filiData?.filiCode && !!selectedBusinessUnit && hasSegmentSelection
  })

  const serviceTypeSourceData = selectedBusinessUnit && hasSegmentSelection ? afterSegmentData : segmentSourceData
  const serviceTypeSourceLoading =
    selectedBusinessUnit && hasSegmentSelection ? afterSegmentLoading : segmentSourceLoading

  // Nome GCE depende do Nome GO selecionado (Business Unit � opcional, entra no filtro s� se selecionado)
  const { data: afterGoNameData, isLoading: afterGoNameLoading } = useQuery({
    queryKey: [
      'preview-management-filters',
      filiData?.filiCode,
      'bu',
      selectedBusinessUnit,
      'directorate',
      directorateKey,
      'portfolio',
      portfolioKey,
      'economicGroup',
      economicGroupKey,
      'segment',
      segmentKey,
      'goName',
      goNameKey
    ],
    queryFn: () =>
      apiPreviewManagement.getPreviewManagementFilters({
        branchCodes: [filiData!.filiCode],
        ...(selectedBusinessUnit ? { businessUnit: [selectedBusinessUnit] } : {}),
        boardOfDirectors: directorate,
        portfolio,
        economicGroup,
        product: segment,
        regionalManager: goName
      }),
    enabled: !!filiData?.filiCode && hasGoNameSelection
  })

  const gceNameSourceData = hasGoNameSelection ? afterGoNameData : serviceTypeSourceData
  const gceNameSourceLoading = hasGoNameSelection ? afterGoNameLoading : serviceTypeSourceLoading

  // Quando uma categoria s� tem 1 op��o real, ela j� vem pr�-selecionada e aplicada
  useEffect(() => {
    if (!baseFiltersData) return

    if (pendingInitialValuesRef.current) {
      pendingInitialValuesRef.current = false
      onFilter(startValues)
      return
    }

    const defaultValues: PreviewManagementFiltersFormProps = {
      businessUnit: getInitialFilterValue(baseFiltersData.businessUnit),
      directorate: getInitialMultiFilterValue(baseFiltersData.boardOfDirectors),
      portfolio: getInitialMultiFilterValue(baseFiltersData.portfolio),
      economicGroup: getInitialMultiFilterValue(baseFiltersData.economicGroup),
      segment: getInitialMultiFilterValue(baseFiltersData.product),
      serviceType: getServiceTypeInitialValue(baseFiltersData.serviceType),
      goName: getInitialMultiFilterValue(baseFiltersData.regionalManager),
      gceName: getInitialMultiFilterValue(baseFiltersData.areaManager)
    }

    methods.reset(defaultValues)
    onFilter(defaultValues)
  }, [baseFiltersData])

  // Ao trocar o Business Unit, reseta Diretoria, Portf�lio, Grupo Econ�mico, Segmento, Tipo de Servi�o, Nome GO e Nome GCE
  const lastBusinessUnitRef = useRef<string | null>(
    startValues.businessUnit && startValues.businessUnit !== 'all' ? startValues.businessUnit : null
  )
  useEffect(() => {
    if (!directorateSourceData || lastBusinessUnitRef.current === selectedBusinessUnit) return

    lastBusinessUnitRef.current = selectedBusinessUnit

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: getInitialMultiFilterValue(directorateSourceData.boardOfDirectors),
      portfolio: getInitialMultiFilterValue(directorateSourceData.portfolio),
      economicGroup: getInitialMultiFilterValue(directorateSourceData.economicGroup),
      segment: getInitialMultiFilterValue(directorateSourceData.product),
      serviceType: getServiceTypeInitialValue(directorateSourceData.serviceType),
      goName: getInitialMultiFilterValue(directorateSourceData.regionalManager),
      gceName: getInitialMultiFilterValue(directorateSourceData.areaManager)
    })
  }, [selectedBusinessUnit, directorateSourceData])

  // Ao trocar a Diretoria, reseta Portf�lio, Grupo Econ�mico, Segmento e Tipo de Servi�o
  const lastDirectorateRef = useRef<string>(toSortedKey(startValues.directorate))
  useEffect(() => {
    if (!portfolioSourceData || lastDirectorateRef.current === directorateKey) return

    lastDirectorateRef.current = directorateKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: getInitialMultiFilterValue(portfolioSourceData.portfolio),
      economicGroup: getInitialMultiFilterValue(portfolioSourceData.economicGroup),
      segment: getInitialMultiFilterValue(portfolioSourceData.product),
      serviceType: getServiceTypeInitialValue(portfolioSourceData.serviceType),
      goName: methods.getValues('goName'),
      gceName: methods.getValues('gceName')
    })
  }, [directorateKey, portfolioSourceData])

  // Ao trocar o Portf�lio, reseta Grupo Econ�mico, Segmento e Tipo de Servi�o
  const lastPortfolioRef = useRef<string>(toSortedKey(startValues.portfolio))
  useEffect(() => {
    if (!economicGroupSourceData || lastPortfolioRef.current === portfolioKey) return

    lastPortfolioRef.current = portfolioKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: methods.getValues('portfolio'),
      economicGroup: getInitialMultiFilterValue(economicGroupSourceData.economicGroup),
      segment: getInitialMultiFilterValue(economicGroupSourceData.product),
      serviceType: getServiceTypeInitialValue(economicGroupSourceData.serviceType),
      goName: methods.getValues('goName'),
      gceName: methods.getValues('gceName')
    })
  }, [portfolioKey, economicGroupSourceData])

  // Ao trocar o Grupo Econ�mico, reseta Segmento e Tipo de Servi�o
  const lastEconomicGroupRef = useRef<string>(toSortedKey(startValues.economicGroup))
  useEffect(() => {
    if (!segmentSourceData || lastEconomicGroupRef.current === economicGroupKey) return

    lastEconomicGroupRef.current = economicGroupKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: methods.getValues('portfolio'),
      economicGroup: methods.getValues('economicGroup'),
      segment: getInitialMultiFilterValue(segmentSourceData.product),
      serviceType: getServiceTypeInitialValue(segmentSourceData.serviceType),
      goName: methods.getValues('goName'),
      gceName: methods.getValues('gceName')
    })
  }, [economicGroupKey, segmentSourceData])

  // Ao trocar o Segmento, reseta Tipo de Servi�o
  const lastSegmentRef = useRef<string>(toSortedKey(startValues.segment))
  useEffect(() => {
    if (!serviceTypeSourceData || lastSegmentRef.current === segmentKey) return

    lastSegmentRef.current = segmentKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: methods.getValues('portfolio'),
      economicGroup: methods.getValues('economicGroup'),
      segment: methods.getValues('segment'),
      serviceType: getServiceTypeInitialValue(serviceTypeSourceData.serviceType),
      goName: methods.getValues('goName'),
      gceName: methods.getValues('gceName')
    })
  }, [segmentKey, serviceTypeSourceData])

  // Tipo de Servi�o com uma �nica op��o real fica for�adamente selecionado, mesmo se a lista de
  // op��es n�o mudar entre trocas de Centro de Lucro / Business Unit / Diretoria / GE / Segmento
  const singleServiceType = getSingleFilterOption(serviceTypeSourceData?.serviceType)
  useEffect(() => {
    if (!singleServiceType || isClearedState()) return
    if (serviceType.length === 1 && serviceType[0] === singleServiceType.code) return

    methods.setValue('serviceType', [singleServiceType.code])
  }, [singleServiceType?.code, serviceType])

  // Ao trocar o Nome GO, reseta Nome GCE
  const lastGoNameRef = useRef<string>(toSortedKey(startValues.goName))
  useEffect(() => {
    if (!gceNameSourceData || lastGoNameRef.current === goNameKey) return

    lastGoNameRef.current = goNameKey

    methods.reset({
      businessUnit: methods.getValues('businessUnit'),
      directorate: methods.getValues('directorate'),
      portfolio: methods.getValues('portfolio'),
      economicGroup: methods.getValues('economicGroup'),
      segment: methods.getValues('segment'),
      serviceType: methods.getValues('serviceType'),
      goName: methods.getValues('goName'),
      gceName: getInitialMultiFilterValue(gceNameSourceData.areaManager)
    })
  }, [goNameKey, gceNameSourceData])

  // Garante a op��o "Todos" (value 'all') mesmo quando o back n�o a devolve na lista
  const toOptions = (items?: { code: string; name: string }[]) => {
    const options = items?.map((item) => ({ label: item.name, value: item.code })) || []
    const hasTodos = options.some((option) => option.value.toLowerCase() === 'all' || option.label.toLowerCase() === 'todos')

    return options.length > 1 && !hasTodos ? [{ label: 'Todos', value: 'all' }, ...options] : options
  }

  const onSubmit = (formData: PreviewManagementFiltersFormProps) => onFilter(formData)

  return (
    <FormProvider {...methods}>
      <RowBox columns={4} marginTop={24} marginBottom={24}>
        <Select
          name="businessUnit"
          label="Business Unit"
          placeholder="Todos"
          options={toOptions(baseFiltersData?.businessUnit)}
          loading={baseFiltersLoading}
        />

        <MultiSelect
          name="directorate"
          label="Diretoria"
          placeholder="Todos"
          options={toOptions(directorateSourceData?.boardOfDirectors)}
          loading={directorateSourceLoading}
        />

        <MultiSelect
          name="portfolio"
          label="Portf�lio"
          placeholder="Todos"
          options={toOptions(portfolioSourceData?.portfolio)}
          loading={portfolioSourceLoading}
        />

        <MultiSelect
          name="economicGroup"
          label="Grupo Econ�mico"
          placeholder="Todos"
          options={toOptions(economicGroupSourceData?.economicGroup)}
          loading={economicGroupSourceLoading}
        />

        <MultiSelect
          name="segment"
          label="Segmento"
          placeholder="Todos"
          options={toOptions(segmentSourceData?.product)}
          loading={segmentSourceLoading}
        />

        <MultiSelect
          name="serviceType"
          label="Tipo de Servi�o"
          placeholder="Todos"
          options={toOptions(serviceTypeSourceData?.serviceType)}
          loading={serviceTypeSourceLoading}
        />

        <MultiSelect
          name="goName"
          label="Nome GO"
          placeholder="Todos"
          options={toOptions(serviceTypeSourceData?.regionalManager)}
          loading={serviceTypeSourceLoading}
        />

        <MultiSelect
          name="gceName"
          label="Nome GCE"
          placeholder="Todos"
          options={toOptions(gceNameSourceData?.areaManager)}
          loading={gceNameSourceLoading}
        />

        {children}

        <S.FilterButtons>
          <Button
            width="100%"
            colorType="outlineBlue"
            rightIcon={<FilterIcon />}
            onClick={() => methods.handleSubmit(onSubmit)()}
          >
            Filtrar
          </Button>

          <Button
            width="100%"
            colorType="outlineBlue"
            rightIcon={<CloseIcon />}
            onClick={() => {
              clearedFiliRef.current = 'ALL'

              // Tudo volta para "Todos" (ou para a �nica op��o real, quando a lista s� tem uma)
              const clearedValues: PreviewManagementFiltersFormProps = {
                ...initialFormValues,
                businessUnit: getInitialFilterValue(baseFiltersData?.businessUnit ?? []),
                directorate: getInitialMultiFilterValue(baseFiltersData?.boardOfDirectors ?? []),
                portfolio: getInitialMultiFilterValue(baseFiltersData?.portfolio ?? []),
                economicGroup: getInitialMultiFilterValue(baseFiltersData?.economicGroup ?? []),
                segment: getInitialMultiFilterValue(baseFiltersData?.product ?? []),
                serviceType: getTodosFilterValue(serviceTypeSourceData?.serviceType),
                goName: getInitialMultiFilterValue(baseFiltersData?.regionalManager ?? []),
                gceName: getInitialMultiFilterValue(baseFiltersData?.areaManager ?? [])
              }

              methods.reset(clearedValues)
              onClear?.()
              onFilter(clearedValues)

              if (filiData && filiData.filiCode !== 'ALL') {
                setFiliData({ ...filiData, filiCode: 'ALL', filiName: 'Todos' })
              }
            }}
          >
            Limpar
          </Button>
        </S.FilterButtons>
      </RowBox>
    </FormProvider>
  )
}
